import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { trackEvent } from "./analytics";
import { trackAdsAddToCart } from "./googleAds";
import { getAnonId } from "./anonId";
import { persistCart, restoreCart, clearPersistedCart } from "./api";

const CartContext = createContext(null);
const KEY = "tc_cart_v1";
// Debounce window for server-side persist. Cart edits often come in
// bursts (qty stepper, multi-item add) — coalesce them into one PATCH.
const PERSIST_DEBOUNCE_MS = 1200;

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  // Token of the shared-cart link the active cart was restored from
  // (if any). Forwarded to /api/checkout/session so we can mark the
  // link as "used" once the resulting order is paid.
  const [sharedCartToken, setSharedCartTokenState] = useState(null);
  // `wasRestored` flips true when we restored items from server on mount.
  // Components like CartRecoveryBanner check this to show a toast.
  const [wasRestored, setWasRestored] = useState(false);
  const persistTimerRef = useRef(null);
  // Once true, server-sync is allowed. We hold off until *after* the
  // restore attempt completes so we don't immediately overwrite a freshly
  // restored cart with an empty one.
  const syncReadyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) Load any local cart first (instant — no network).
      let localItems = [];
      try {
        const raw = localStorage.getItem(KEY);
        if (raw) localItems = JSON.parse(raw);
        const tk = localStorage.getItem("tc_shared_cart_token");
        if (tk) setSharedCartTokenState(tk);
      } catch { /* ignore */ }

      // 2) If the local cart is empty, try to restore from server.
      //    If the local cart has items, the local wins (visitor was
      //    actively editing) — server-sync below will reconcile.
      if (localItems.length === 0) {
        try {
          const anonId = getAnonId();
          const resp = await restoreCart(anonId);
          if (!cancelled && Array.isArray(resp?.items) && resp.items.length > 0) {
            // Stamp client-side _cid so existing render machinery works.
            const stamped = resp.items.map((it) => ({
              ...it,
              _cid: Date.now() + Math.random(),
            }));
            localItems = stamped;
            setWasRestored(true);
            trackEvent("cart_restored", {
              item_count: stamped.length,
              age_iso: resp?.updated_at,
            });
          }
        } catch {
          // Network failure is non-fatal — first-visit visitors hit this
          // branch too. Carry on with the empty local cart.
        }
      }

      if (!cancelled) {
        setItems(localItems);
        setHydrated(true);
        // Allow server-sync from here on. Wrap in setTimeout(0) so the
        // initial `useEffect[items]` save runs AFTER React commits the
        // restored items — otherwise we'd skip persisting a restored cart.
        setTimeout(() => { syncReadyRef.current = true; }, 0);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Local-storage mirror (kept for offline browsing + page refresh speed).
  useEffect(() => {
    if (!hydrated) return;
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items, hydrated]);

  // Server-side persist (debounced). Fires whenever the cart changes
  // after initial hydration. Empty carts also persist so the row reflects
  // "customer deliberately emptied" — but we use DELETE for that case
  // since "no row" and "empty row" are equivalent for restore semantics.
  useEffect(() => {
    if (!hydrated || !syncReadyRef.current) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(async () => {
      try {
        const anonId = getAnonId();
        if (items.length === 0) {
          // Empty cart → clear the server row (avoids restoring an empty
          // cart on next visit and confusing the recovery email job).
          await clearPersistedCart(anonId);
          return;
        }
        const subtotal = items.reduce(
          (acc, it) => acc + Number(it.unit_price || 0) * Number(it.quantity || 1),
          0,
        );
        await persistCart(anonId, items, subtotal);
      } catch {
        // Persistence failures are silent — local cart still works.
      }
    }, PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [items, hydrated]);

  const addItem = useCallback((item) => {
    setItems((prev) => [...prev, { ...item, _cid: Date.now() + Math.random() }]);
    trackEvent("add_to_cart", {
      currency: "CAD",
      value: Number(item.unit_price || 0) * Number(item.quantity || 1),
      items: [{
        item_id: item.product_id || "item",
        item_name: item.product_name || "Item",
        item_category: item.product_category || item.type || "merch",
        item_variant: item.size || item.selected_color || undefined,
        price: Number(item.unit_price || 0),
        quantity: Number(item.quantity || 1),
      }],
    });
    trackAdsAddToCart({
      value: Number(item.unit_price || 0) * Number(item.quantity || 1),
      currency: "CAD",
    });
  }, []);

  const updateQty = useCallback((cid, qty) => {
    setItems((prev) => prev.map((it) => (it._cid === cid ? { ...it, quantity: Math.max(1, qty) } : it)));
  }, []);

  const removeItem = useCallback((cid) => {
    setItems((prev) => prev.filter((it) => it._cid !== cid));
  }, []);

  /**
   * Replace the entire cart line identified by `cid` with `nextFields`.
   * Used by the customizer's "Edit in cart" flow (?edit_cid=...) so a
   * customer can tweak their mockup post add-to-cart without losing the
   * cart's other items. Preserves `_cid` so the slot stays in place.
   */
  const replaceItem = useCallback((cid, nextFields) => {
    // _cid in cart items is a JS number (Date.now() + Math.random()), but
    // editCid coming from the URL `?edit_cid=` query param is a string. We
    // need to coerce so the match doesn't silently fail (which used to
    // result in "edit didn't save").
    const cidKey = String(cid);
    setItems((prev) => prev.map((it) => (String(it._cid) === cidKey ? { ...nextFields, _cid: it._cid } : it)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setSharedCartTokenState(null);
    setWasRestored(false);
    try { localStorage.removeItem("tc_shared_cart_token"); } catch { /* ignore */ }
    // Server-side row gets cleared by the debounced effect above.
  }, []);

  /**
   * Replace the cart entirely with `nextItems` (used by the shared-cart
   * landing page after the customer chooses "Replace my cart"). Each
   * item is given a fresh client-side `_cid` so the existing rendering
   * machinery (qty buttons, remove buttons) works unchanged.
   */
  const replaceItems = useCallback((nextItems) => {
    const stamped = (nextItems || []).map((it) => ({ ...it, _cid: Date.now() + Math.random() }));
    setItems(stamped);
  }, []);

  /** Merge `extraItems` onto the existing cart, preserving the current items. */
  const mergeItems = useCallback((extraItems) => {
    const stamped = (extraItems || []).map((it) => ({ ...it, _cid: Date.now() + Math.random() }));
    setItems((prev) => [...prev, ...stamped]);
  }, []);

  const setSharedCartToken = useCallback((token) => {
    setSharedCartTokenState(token || null);
    try {
      if (token) localStorage.setItem("tc_shared_cart_token", token);
      else localStorage.removeItem("tc_shared_cart_token");
    } catch { /* ignore */ }
  }, []);

  /** Mark the restoration banner as dismissed (consumed). */
  const acknowledgeRestore = useCallback(() => setWasRestored(false), []);

  return (
    <CartContext.Provider value={{
      items, addItem, updateQty, removeItem, clearCart,
      replaceItem, replaceItems, mergeItems,
      sharedCartToken, setSharedCartToken,
      wasRestored, acknowledgeRestore,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}
